/**
 * El logotipo, construido según el Manual de Marca.
 *
 * «El logotipo se construye, no se dibuja»: un cuadrado de tinta de 28 px de
 * lado con radio 2 px, una «C» en Geist Bold centrada, y el wordmark CRITERO en
 * Newsreader Medium separado 8 px. Toda proporción se deriva del cuadrado.
 *
 * Lo que había antes era un símbolo distinto —dos caminos convergiendo en un
 * vértice, en morado— de la identidad de 2025. No es una variante de este: es
 * otro logo, y el manual no lo recoge.
 *
 * Usos incorrectos que el manual señala y que aquí se respetan: el cuadrado
 * nunca es un círculo, nunca se tiñe de oro, teal ni terracota, y el wordmark
 * nunca va en negrita, cursiva ni caja baja.
 */

const MEDIDAS = {
  // Tamaños oficiales del manual. `sm` es el del pie.
  sm: { lado: 24, letra: "0.8125rem", palabra: "1.125rem" },
  md: { lado: 28, letra: "0.9375rem", palabra: "1.25rem" },
} as const;

export function Logo({
  size = "md",
  withWordmark = true,
  onDark = false,
}: {
  size?: keyof typeof MEDIDAS;
  withWordmark?: boolean;
  onDark?: boolean;
}) {
  const m = MEDIDAS[size];

  return (
    <span className="inline-flex items-center gap-2" aria-label="CRITERO" role="img">
      <span
        aria-hidden="true"
        // Sobre tinta se invierte el cuadrado, nunca el color de la «C»
        // respecto a su fondo.
        className={
          "flex shrink-0 items-center justify-center rounded-marca " +
          (onDark ? "bg-paper text-ink" : "bg-ink text-paper")
        }
        style={{ width: m.lado, height: m.lado }}
      >
        <span
          className="font-sans font-bold"
          // El tracking de la «C» desplaza el glifo a la derecha; el sangrado
          // lo devuelve al centro óptico del cuadrado.
          style={{ fontSize: m.letra, letterSpacing: "0.05em", textIndent: "0.05em" }}
        >
          C
        </span>
      </span>
      {withWordmark ? (
        <span
          className={"font-display " + (onDark ? "text-paper" : "text-ink")}
          style={{ fontSize: m.palabra, fontWeight: 500, letterSpacing: "-0.01em" }}
        >
          CRITERO
        </span>
      ) : null}
    </span>
  );
}
